import {
  DynamicField,
  FormularioDinamicoRuntime,
  PerguntaInline
} from '../../type/FormularioDinamico';

export const valoresPorInstancia = (
  formularios: FormularioDinamicoRuntime[]
): Record<string, Record<string, unknown>> =>
  formularios.reduce<Record<string, Record<string, unknown>>>(
    (acumulado, formulario) => ({
      ...acumulado,
      [formulario.id]: formulario.values || {}
    }),
    {}
  );

export const estruturaVazia = (
  fields: DynamicField[]
): Record<string, unknown> =>
  (fields ?? []).reduce<Record<string, unknown>>((acumulado, campo) => {
    const chave = campo?.controlName ?? campo?.label;

    if (!chave) {
      return acumulado;
    }

    return {
      ...acumulado,
      [chave]: campo.fields?.length > 0 ? estruturaVazia(campo.fields) : null
    };
  }, {});

const vazio = (valor: unknown) =>
  valor === null ||
  valor === undefined ||
  valor === '' ||
  (Array.isArray(valor) && valor.length === 0) ||
  (!!valor &&
    typeof valor === 'object' &&
    Object.keys(valor as Record<string, unknown>).length === 0);

const normalizar = (valor: unknown): unknown => {
  if (Array.isArray(valor)) {
    return valor.map(normalizar);
  }

  if (typeof valor === 'string') {
    const texto = valor.trim();

    if (!texto.startsWith('{') && !texto.startsWith('[')) {
      return valor;
    }

    try {
      return normalizar(JSON.parse(texto));
    } catch {
      return valor;
    }
  }

  if (!!valor && typeof valor === 'object') {
    const objeto = valor as Record<string, unknown>;

    return Object.keys(objeto)
      .sort()
      .reduce<Record<string, unknown>>((acumulado, chave) => {
        const normalizado = normalizar(objeto[chave]);

        return vazio(normalizado)
          ? acumulado
          : { ...acumulado, [chave]: normalizado };
      }, {});
  }

  return valor;
};

const TIPO_NOME = 'FULL_NAME';

const semValor = (valor: unknown) =>
  valor === null || valor === undefined || valor === '';

export const aplicarPadroes = (
  fields: DynamicField[],
  values: Record<string, unknown>,
  nomeUsuario?: string
): Record<string, unknown> =>
  (fields ?? []).reduce<Record<string, unknown>>((acumulado, campo) => {
    const chave = campo?.controlName ?? campo?.label;

    if (!chave) {
      return acumulado;
    }

    if (campo.fields?.length > 0) {
      const grupo = acumulado[chave];

      return {
        ...acumulado,
        [chave]: aplicarPadroes(
          campo.fields,
          (typeof grupo === 'object' && grupo !== null ? grupo : {}) as Record<
            string,
            unknown
          >,
          nomeUsuario
        )
      };
    }

    if (
      campo.type !== TIPO_NOME ||
      !nomeUsuario ||
      !semValor(acumulado[chave])
    ) {
      return acumulado;
    }

    return { ...acumulado, [chave]: nomeUsuario };
  }, values);

export const valoresAplicaveis = (
  formularios: FormularioDinamicoRuntime[],
  nomeUsuario?: string
): Record<string, Record<string, unknown>> =>
  formularios.reduce<Record<string, Record<string, unknown>>>(
    (acumulado, formulario) => ({
      ...acumulado,
      [formulario.id]: aplicarPadroes(
        formulario.fields,
        formulario.values || {},
        nomeUsuario
      )
    }),
    {}
  );

export const mesmoConteudo = (a: unknown, b: unknown) =>
  JSON.stringify(normalizar(a)) === JSON.stringify(normalizar(b));

export const paraPerguntaInline = (campo: DynamicField): PerguntaInline => ({
  id: campo.id,
  controlName: campo.controlName,
  label: campo.label ?? '',
  tipo: campo.type ?? '',
  placeholder: campo.placeholder,
  descricao: campo.description,
  mascara: campo.mask,
  obrigatorio: !!campo.required,
  opcoes: campo.options ?? [],
  config: campo.config ?? {},
  conditions: campo.conditions ?? [],
  perguntas: (campo.fields ?? []).map(paraPerguntaInline)
});
